import { RestaurantRecipeDTO } from '../../../../types/restaurant/RestaurantRecipeTypes';
import RestaurantTable, {
  RestaurantTableColumn,
} from '../../shared/RestaurantTable';
import RestaurantTableActions from '../../shared/RestaurantTableActions';

interface Props {
  recipes: RestaurantRecipeDTO[];
  onEdit: (recipe: RestaurantRecipeDTO) => void;
  onDeactivate: (recipe: RestaurantRecipeDTO) => void;
}

const formatQuantity = (value: number | null, unitCode?: string | null) =>
  `${Number(value ?? 0).toLocaleString('es-SV', {
    maximumFractionDigits: 6,
  })} ${unitCode || ''}`.trim();

const RecipeTable = ({ recipes, onEdit, onDeactivate }: Props) => {
  const columns: RestaurantTableColumn<RestaurantRecipeDTO>[] = [
    {
      key: 'menuItem',
      header: 'Producto del menú',
      cell: (recipe) => (
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">
            {recipe.menuItemCode || 'Sin código'}
          </p>
          <p className="truncate text-sm text-black dark:text-white">
            {recipe.menuItemName || 'Sin producto'}
          </p>
        </div>
      ),
    },
    {
      key: 'name',
      header: 'Receta',
      className: 'font-medium text-black dark:text-white',
      cell: (recipe) => recipe.name,
    },
    {
      key: 'description',
      header: 'Descripción',
      className: 'max-w-sm text-sm text-bodydark2',
      cell: (recipe) => (
        <span className="line-clamp-2">
          {recipe.description || 'Sin descripción'}
        </span>
      ),
    },
    {
      key: 'yield',
      header: 'Rendimiento',
      className: 'whitespace-nowrap text-sm text-black dark:text-white',
      cell: (recipe) =>
        formatQuantity(recipe.yieldQuantity, recipe.yieldUnitCode),
    },
    {
      key: 'ingredients',
      header: 'Ingredientes',
      className: 'text-center font-semibold text-black dark:text-white',
      headClassName: 'text-center',
      cell: (recipe) => recipe.ingredientCount,
    },
    {
      key: 'status',
      header: 'Estado',
      cell: (recipe) => (
        <span
          className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${
            recipe.active
              ? 'border-success/20 bg-success/10 text-success'
              : 'border-danger/20 bg-danger/10 text-danger'
          }`}
        >
          {recipe.active ? 'Activa' : 'Inactiva'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Acciones',
      className: 'text-right',
      headClassName: 'text-right',
      cell: (recipe) => (
        <RestaurantTableActions
          onEdit={() => onEdit(recipe)}
          onToggle={recipe.active ? () => onDeactivate(recipe) : undefined}
          active={recipe.active}
          toggleLabel={{ on: 'Desactivar', off: 'Reactivar' }}
        />
      ),
    },
  ];

  return (
    <RestaurantTable
      columns={columns}
      rows={recipes}
      rowKey={(recipe) => recipe.id}
      rowClassName={(recipe) => (recipe.active ? '' : 'opacity-65')}
      minWidth="min-w-[1050px]"
    />
  );
};

export default RecipeTable;
